import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import type { FrontendThemeConfiguration } from '@tourfold/libraries/tf-api-client-frontend-v2';

export type ThemePreviewMode = 'light' | 'dark';
export type ThemePreviewSize = 'mini' | 'card' | 'large';
export type ThemePreviewVariant = 'app' | 'login';

@Component({
  selector: 'tf-settings-theme-preview',
  imports: [NgStyle],
  templateUrl: './theme-preview.component.html',
  styleUrl: './theme-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThemePreviewComponent {
  readonly theme = input.required<FrontendThemeConfiguration>();
  readonly mode = input<ThemePreviewMode>('light');
  readonly size = input<ThemePreviewSize>('card');
  readonly variant = input<ThemePreviewVariant>('app');
  readonly interactive = input(false);

  readonly values = computed(() => this.theme()[this.mode()]);

  /** Maps the persisted font-style key to a concrete CSS font stack for the preview. */
  private static readonly FONT_FAMILIES: Record<string, string> = {
    sora: "'Sora', sans-serif",
    hind_madurai: "'Hind Madurai', sans-serif",
  };

  readonly cssVars = computed<Record<string, string>>(() => {
    const theme = this.values();
    return {
      '--tp-primary': theme.primaryColor,
      '--tp-accent': theme.accentColor,
      '--tp-text': theme.textColor,
      '--tp-text-muted': theme.textMutedColor,
      '--tp-border': theme.borderColor,
      '--tp-border-strong': theme.borderStrongColor,
      '--tp-page-bg': theme.surface.pageBackgroundColor,
      '--tp-public-bg': theme.surface.publicPageBackgroundColor,
      '--tp-panel-bg': theme.surface.panelBackgroundColor,
      '--tp-bar-bg': theme.surface.barBackgroundColor,
      '--tp-list-bg': theme.surface.listBackgroundColor,
      '--tp-input-bg': theme.surface.inputBackgroundColor,
      '--tp-hover-bg': theme.surface.hoverBackgroundColor,
      '--tp-selected-bg': theme.surface.selectedBackgroundColor,
      '--tp-header-bg': theme.header.backgroundColor,
      '--tp-header-text': theme.header.foregroundColor,
      '--tp-sidebar-bg': theme.sidebar.backgroundColor,
      '--tp-sidebar-text': theme.sidebar.foregroundColor,
      '--tp-sidebar-header-bg': theme.sidebar.headerBackgroundColor,
      '--tp-sidebar-header-text': theme.sidebar.headerForegroundColor,
      '--tp-sidebar-active-bg': theme.sidebar.activeBackgroundColor,
      '--tp-sidebar-active-text': theme.sidebar.activeForegroundColor,
      '--tp-sidebar-indicator': theme.sidebar.activeIndicatorColor,
      '--tp-primary-text': this.mode() === 'dark' ? '#0b0f17' : '#ffffff',
      '--tp-font-family':
        ThemePreviewComponent.FONT_FAMILIES[theme.baseFontStyle] ??
        'var(--p-font-family, system-ui, sans-serif)',
    };
  });

  readonly rows: ReadonlyArray<{
    label: string;
    type: string;
    status: 'verified' | 'pending' | 'failed';
    state: 'normal' | 'hover' | 'selected';
  }> = [
    { label: 'M. Hofbauer', type: 'Driver', status: 'verified', state: 'normal' },
    { label: 'A. Schmid', type: 'Dispatcher', status: 'pending', state: 'hover' },
    { label: 'L. Toth', type: 'Driver', status: 'verified', state: 'selected' },
    { label: 'R. Ng', type: 'Assistant', status: 'failed', state: 'normal' },
    { label: 'C. Reiter', type: 'Driver', status: 'verified', state: 'normal' },
  ];

  readonly navItems = ['Tours', 'Backlog', 'Objects', 'Brands', 'Themes'];

  readonly tabs = ['All', 'Active', 'Archived'];

  readonly tableColumns: ReadonlyArray<{ label: string; sortable: boolean; actions?: boolean }> = [
    { label: 'Name', sortable: true },
    { label: 'Type', sortable: true },
    { label: 'Status', sortable: true },
    { label: 'Actions', sortable: false, actions: true },
  ];

  statusClass(status: 'verified' | 'pending' | 'failed'): string {
    return `theme-preview__status theme-preview__status--${status}`;
  }

  rowClass(state: 'normal' | 'hover' | 'selected'): string {
    return `theme-preview__row theme-preview__row--${state}`;
  }

  rowLimit(): number {
    return this.size() === 'large' ? 5 : 4;
  }

  navLimit(): number {
    return this.size() === 'mini' ? 3 : 5;
  }

  showLabels(): boolean {
    return this.size() !== 'mini';
  }

  /**
   * The full list-page mockup (tabs, toolbar, table header + rows, pagination) is only rendered in
   * the `large` variant, which is meant to showcase the whole theme option set in a realistic
   * screen. `mini` / `card` keep the compact mockup.
   */
  showCatalog(): boolean {
    return this.size() === 'large';
  }
}
